import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const AboutUs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const values = [
    { icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png', title: 'Excellence', description: 'We strive for excellence in every book we help publish, ensuring the highest quality standards.' },
    { icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png', title: 'Integrity', description: 'Honest, transparent relationships with our authors built on trust and respect.' },
    { icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png', title: 'Innovation', description: 'Embracing new technologies and methods to give authors the best publishing experience.' },
    { icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png', title: 'Author-First', description: 'Every decision we make puts the author and their vision at the center.' }
  ];

  const team = [
    { name: 'Sarah Johnson', role: 'Founder & CEO', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop', bio: '15 years in publishing, passionate about helping authors succeed.' },
    { name: 'Michael Chen', role: 'Editorial Director', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop', bio: 'Award-winning editor with expertise across all genres.' },
    { name: 'Emily Rodriguez', role: 'Marketing Director', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop', bio: 'Digital marketing expert specializing in book promotion.' },
    { name: 'David Thompson', role: 'Design Director', image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop', bio: 'Creative director with 50+ award-winning book covers.' }
  ];

  const milestones = [
    { year: '2015', title: 'Company Founded', description: 'Started with a mission to empower independent authors' },
    { year: '2017', title: '100 Books Published', description: 'Reached our first major milestone with 100 successful publications' },
    { year: '2019', title: 'International Expansion', description: 'Expanded services to authors worldwide' },
    { year: '2021', title: '1,000 Books Milestone', description: 'Celebrated publishing our 1,000th book' },
    { year: '2023', title: 'Industry Recognition', description: 'Awarded Best Independent Publisher of the Year' },
    { year: '2024', title: '2,500+ Books Published', description: 'Continuing to grow and serve authors globally' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Empowering Authors to Share Their <span className="text-amber-700">Stories</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Since 2015, we've helped over 2,500 authors transform their manuscripts into professionally published books. We're more than a publishing company – we're your partner in bringing stories to life.
            </p>
            <Button onClick={() => setIsModalOpen(true)} className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
              Start Your Publishing Journey
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-700 mb-2">2,500+</div>
              <div className="text-gray-600">Books Published</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-700 mb-2">9</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-700 mb-2">98%</div>
              <div className="text-gray-600">Author Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-700 mb-2">50+</div>
              <div className="text-gray-600">Team Members</div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our <span className="text-amber-700">Story</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Rushmore Publishing was founded in 2015 by a group of publishing professionals who believed that every author deserves access to world-class publishing services. We saw too many talented writers struggle with traditional publishing barriers and expensive vanity presses.
                </p>
                <p>
                  We set out to create a different kind of publishing company – one that combines the quality of traditional publishing with the speed and author control of self-publishing. Our mission is simple: help authors publish professional books they're proud of.
                </p>
                <p>
                  Today, we're proud to have published over 2,500 books across every genre imaginable. From first-time authors to established writers, we treat every project with the care and attention it deserves.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=600&fit=crop" alt="Our story" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-amber-700">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl text-center hover:shadow-xl transition-all">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 p-4">
                  <img src={value.icon} alt={value.title} className="w-full h-full object-contain" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Meet Our <span className="text-amber-700">Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Experienced professionals dedicated to your success</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <img src={member.image} alt={member.name} className="w-full h-64 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-amber-700 font-semibold mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-amber-700">Journey</span>
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-8 mb-12 last:mb-0">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-full bg-amber-700 text-white flex items-center justify-center text-2xl font-bold">
                    {milestone.year}
                  </div>
                </div>
                <div className="flex-1 pt-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{milestone.title}</h3>
                  <p className="text-gray-600">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&h=800&fit=crop" alt="Join us" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">Ready to Publish Your Book?</h2>
            <p className="text-xl mb-12 opacity-90">Join thousands of authors who have trusted us with their publishing journey.</p>
            <Button onClick={() => setIsModalOpen(true)} className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all">
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AboutUs;